import TextField from "@mui/material/TextField";
import DatePickerFooter from "components-redesign/DatetimeInput/DatePickerFooter";
import DatePickerHeader from "components-redesign/DatetimeInput/DatePickerHeader";
import TimePicker from "components-redesign/DatetimeInput/TimePicker";
import { FIGMA_COLORS } from "components-redesign/styles/colors";
import { makeStyles } from "components/mui-core";
import {
  convertDateTimeToSameISO,
  convertDateToSameISO,
  convertISOtoSameDate,
  convertISOtoSameDateTime,
  endOfIsoDay,
  startOfIsoDay,
} from "helpers";
import { ISOstring } from "model/modelTypes";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { hexToRgb } from "vendor/material-dashboard-pro-react-v1/assets/jss/material-dashboard-pro-react";
import {
  RANGE_ERRORS,
  dateErrorTextSx,
  displayToIso,
  isoToDisplay,
  outlinedDateTextFieldSx,
  validateDateInput,
} from "./utils";

const useStyles = makeStyles()({
  calendar: {
    fontFamily: "Roboto, Helvetica, Arial, sans-serif !important",
    zIndex: "99999 !important",
    borderColor: "transparent !important",
    display: "flex",
    flexDirection: "column",
    "& .react-datepicker": {
      boxShadow: "none !important",
      "&__header": {
        backgroundColor: "#fff !important",
        borderBottom: "none !important",
        padding: 0,
        borderRadius: "16px !important",
      },
      "&__day": {
        height: "40px",
        width: "40px",
        lineHeight: "40px",
        fontWeight: 300,
        borderRadius: "50%",
        fontSize: "16px",
        "&:hover": {
          borderRadius: "50%",
          backgroundColor: "#f0f0f0",
        },
      },
      "&__current-month, &__day-name": {
        color: `${FIGMA_COLORS.onSurfaceLight} !important`,
        fontFamily: "Roboto",
      },
      "&__day--outside-month": {
        color: `rgba(${hexToRgb(
          FIGMA_COLORS.surfaceVariantDark
        )}, .38) !important`,
      },
      "&__day-name": {
        padding: "10px 0",
        fontSize: 16,
        fontWeight: 400,
        lineHeight: "24px",
        width: "40px",
      },
      "&__day--in-range, &__day--in-selecting-range": {
        borderRadius: "0 !important",
        backgroundColor: `${FIGMA_COLORS.primaryContainerLight} !important`,
        color: `${FIGMA_COLORS.onSecondaryContainer}`,
        boxShadow: `10px 0 0 ${FIGMA_COLORS.primaryContainerLight}, -10px 0 0 ${FIGMA_COLORS.primaryContainerLight}`,
      },
      "&__day--selected, &__day--range-start, &__day--selecting-range-start": {
        position: "relative",
        zIndex: "1",
        borderRadius: "50% !important",
        backgroundColor: `${FIGMA_COLORS.primaryLight} !important`,
        color: `#fff !important`,
        boxShadow: "none !important",
      },
      "&__day--selected,  &__day--range-end, &__day--selecting-range-end": {
        position: "relative",
        zIndex: "1",
        borderRadius: "50% !important",
        backgroundColor: `${FIGMA_COLORS.primaryLight} !important`,
        color: `#fff !important`,
        boxShadow: "none !important",
      },
      "&__day--keyboard-selected": {
        background: "none",
      },
      "&__day--range-start + .react-datepicker__day--in-range, &__day--selecting-range-start + .react-datepicker__day--in-selecting-range":
        {
          boxShadow: `10px 0 0 ${FIGMA_COLORS.primaryContainerLight}, -25px 0 0 ${FIGMA_COLORS.primaryContainerLight}`,
        },
      "&__day--in-range:has(+ .react-datepicker__day--range-end), &__day--in-selecting-range:has(+ .react-datepicker__day--selecting-range-end)":
        {
          boxShadow: `25px 0 0 ${FIGMA_COLORS.primaryContainerLight}, -10px 0 0 ${FIGMA_COLORS.primaryContainerLight}`,
        },
      "&__day--range-start + .react-datepicker__day--in-range:has(+ .react-datepicker__day--range-end), &__day--selecting-range-start + .react-datepicker__day--in-selecting-range:has(+ .react-datepicker__day--selecting-range-end)":
        {
          boxShadow: `25px 0 0 ${FIGMA_COLORS.primaryContainerLight}, -25px 0 0 ${FIGMA_COLORS.primaryContainerLight}`,
        },
    },
  },
  disabled: { opacity: 0.5, pointerEvents: "none" },
  timeSection: {
    display: "flex",
    gap: "24px",
    borderTop: "1px solid rgba(15, 20, 22, 0.12)",
    borderBottom: "1px solid rgba(15, 20, 22, 0.12)",
    padding: "16px",
  },
  dateInputsRow: {
    display: "flex",
    gap: "12px",
    padding: "12px 12px 0",
  },
  dateInputWrapper: {
    width: 147,
  },
  dateTextField: outlinedDateTextFieldSx,
  errorText: dateErrorTextSx,
});

type TDatePickerValue = Date | null | Array<Date | null>;

function valueToFakeUtc(
  value: Array<ISOstring | null>,
  isUsingTime?: boolean
): TDatePickerValue {
  return value.map((v) => {
    if (!v) return null;
    return isUsingTime ? convertISOtoSameDateTime(v) : convertISOtoSameDate(v);
  }) as TDatePickerValue;
}

function fakeUtcToValue(
  date: TDatePickerValue,
  isUsingTime?: boolean
): Array<ISOstring | null> {
  if (Array.isArray(date)) {
    return date.map((d) => {
      if (!d) return null;
      return isUsingTime
        ? convertDateTimeToSameISO(d)
        : convertDateToSameISO(d);
    });
  }
  return [null, null];
}

type TRangeValue = [ISOstring | null, ISOstring | null];

interface RangeDatePickerProps {
  className?: string;
  value: TRangeValue;
  maxDate?: ISOstring;
  minDate?: ISOstring;
  /**
   * Why a date past `maxDate` is refused. Defaults to the reporting wording,
   * which only reads correctly when `maxDate` is today.
   */
  maxDateError?: string;
  onSelect: (value: TRangeValue) => void;
  onSubmit: (value: [ISOstring, ISOstring]) => void;
  onClear?: () => void;
  initialValue?: TRangeValue;
  onCancel: () => void;
  inline?: boolean;
  disabled?: boolean;
  disableStartDate?: boolean;
  disableEndDate?: boolean;
  hideInputs?: boolean;
  hideEndInput?: boolean;
  showTime?: boolean;
  /** Days for which this returns false are not selectable. Receives an ISO date. */
  isDateSelectable?: (date: ISOstring) => boolean;
  /** Hover text for a day, e.g. why it cannot be selected. */
  dayTooltip?: (date: ISOstring) => string | undefined;
}

const toStartOfDay = (iso: ISOstring | null | undefined) =>
  iso ? (startOfIsoDay(iso) as ISOstring) : undefined;

const toEndOfDay = (iso: ISOstring | null | undefined) =>
  iso ? (endOfIsoDay(iso) as ISOstring) : undefined;

const getTimeParts = (iso: ISOstring | null, fallback: [string, string]) => {
  const timePart = iso?.split("T")[1];
  if (!timePart) return fallback;
  const [h, m] = timePart.split(":");
  return [h, m] as [string, string];
};

const RangeDatePicker = ({
  className,
  value,
  maxDate,
  minDate,
  maxDateError,
  onSelect,
  onSubmit,
  onCancel,
  onClear,
  initialValue,
  inline,
  disabled,
  disableStartDate = false,
  disableEndDate = false,
  hideInputs = false,
  hideEndInput = false,
  showTime = false,
  isDateSelectable,
  dayTooltip,
}: RangeDatePickerProps) => {
  const { classes: ownClasses, cx } = useStyles();

  const [startInput, setStartInput] = useState(() => isoToDisplay(value[0]));
  const [endInput, setEndInput] = useState(() => isoToDisplay(value[1]));
  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);

  // Time state is initialized once from the incoming value (00:00 start / 23:59
  // end defaults) and is intentionally NOT re-synced on calendar day changes.
  const [startTime, setStartTime] = useState(() =>
    getTimeParts(value[0], ["00", "00"])
  );
  const [endTime, setEndTime] = useState(() =>
    getTimeParts(value[1], ["23", "59"])
  );
  // Bumped on Clear to remount the TimePickers and reset their inputs.
  const [timeResetKey, setTimeResetKey] = useState(0);

  const handleStartTimeChange = useCallback((h: string, m: string) => {
    setStartTime([h, m]);
  }, []);

  const handleEndTimeChange = useCallback((h: string, m: string) => {
    setEndTime([h, m]);
  }, []);

  useEffect(() => {
    setStartInput(isoToDisplay(value[0]));
    setStartError(null);
  }, [value[0]]);

  useEffect(() => {
    setEndInput(isoToDisplay(value[1]));
    setEndError(null);
  }, [value[1]]);

  const validateInput = useCallback(
    (
      raw: string,
      rangeFloor?: ISOstring | null,
      rangeCeiling?: ISOstring | null
    ) => {
      const boundError = validateDateInput(
        raw,
        toStartOfDay(minDate),
        toEndOfDay(maxDate),
        {
          tooEarly: minDate
            ? `Date cannot be before ${isoToDisplay(minDate)}`
            : undefined,
          tooLate: maxDateError ?? "Future dates are not allowed",
        }
      );
      if (boundError) return boundError;

      const rangeError = validateDateInput(
        raw,
        toStartOfDay(rangeFloor),
        toEndOfDay(rangeCeiling),
        RANGE_ERRORS
      );
      if (rangeError) return rangeError;

      const iso = displayToIso(raw);
      if (iso && isDateSelectable && !isDateSelectable(iso)) {
        return dayTooltip?.(iso) ?? "Date is not available";
      }
      return null;
    },
    [minDate, maxDate, maxDateError, isDateSelectable, dayTooltip]
  );

  const handleStartInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setStartInput(raw);
      const error = validateInput(raw, undefined, value[1]);
      setStartError(error);
      if (!raw) {
        onSelect([null, value[1]]);
        return;
      }
      if (error) return;
      const iso = displayToIso(raw);
      if (!iso) return;

      if (endError) {
        const revalidatedEndError = validateInput(endInput, iso, undefined);
        setEndError(revalidatedEndError);
        const endIso = revalidatedEndError ? null : displayToIso(endInput);
        if (endIso) {
          onSelect([iso, endIso]);
          return;
        }
      }
      onSelect([iso, value[1]]);
    },
    [validateInput, onSelect, value, endError, endInput]
  );

  const handleEndInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setEndInput(raw);
      const error = validateInput(raw, value[0], undefined);
      setEndError(error);
      if (!raw) {
        onSelect([value[0], null]);
        return;
      }
      if (error) return;
      const iso = displayToIso(raw);
      if (!iso) return;

      if (startError) {
        const revalidatedStartError = validateInput(startInput, undefined, iso);
        setStartError(revalidatedStartError);
        const startIso = revalidatedStartError
          ? null
          : displayToIso(startInput);
        if (startIso) {
          onSelect([startIso, iso]);
          return;
        }
      }
      onSelect([value[0], iso]);
    },
    [validateInput, onSelect, value, startError, startInput]
  );

  const minDateFakeUtc = minDate ? convertISOtoSameDate(minDate) : undefined;
  const maxDateFakeUtc = maxDate ? convertISOtoSameDate(maxDate) : undefined;

  const valueFakeUtc = valueToFakeUtc(value, false) as [
    Date | null,
    Date | null,
  ];
  const startDate = valueFakeUtc ? valueFakeUtc[0] : null;
  const endDate = valueFakeUtc ? valueFakeUtc[1] : null;

  // When start is locked/absent but an end date is selected, show the end date
  // as a single selected circle. Using `selected` (not `startDate`) avoids
  // react-datepicker's "selecting-range" hover behavior which would draw a range
  // from the shown date to wherever the cursor moves.
  const isEndOnly = disableStartDate && !startDate;
  const calendarSelected = isEndOnly && endDate ? endDate : startDate;
  const calendarRangeStart = isEndOnly ? null : startDate;
  const calendarRangeEnd = isEndOnly ? null : endDate;

  const handleOnChange = (date: [Date | null, Date | null]) => {
    const [start, end] = date;
    let result: [ISOstring | null, ISOstring | null];

    if (disableStartDate && disableEndDate) {
      // Both dates locked — calendar clicks should have no effect.
      result = [value[0], value[1]];
    } else if (disableStartDate && !disableEndDate) {
      // When start is locked and the existing startDate is before minDate,
      // react-datepicker resets the range and calls onChange([clickedDate, null])
      // instead of onChange([lockedStart, clickedDate]). Use end ?? start to
      // extract the clicked date in both cases.
      const clickedDate = end ?? start;
      const endDateISO = clickedDate ? convertDateToSameISO(clickedDate) : null;
      const startDateISO = value[0];
      result = [startDateISO, endDateISO];
    } else if (disableEndDate && !disableStartDate) {
      const startDateISO = start ? convertDateToSameISO(start) : null;
      const endDateISO = value[1];
      result = [startDateISO, endDateISO];
    } else {
      const normalResult = fakeUtcToValue([start, end], false);
      result = [normalResult[0] || null, normalResult[1] || null];
    }

    onSelect(result);
  };

  const handleClear = () => {
    setStartInput("");
    setEndInput("");
    setStartError(null);
    setEndError(null);
    setStartTime(["00", "00"]);
    setEndTime(["23", "59"]);
    setTimeResetKey((k) => k + 1);
    onSelect([null, null]);
    onClear?.();
  };

  const handleCancel = () => {
    const resetValue = initialValue ?? [null, null];
    setStartInput(isoToDisplay(resetValue[0]));
    setEndInput(isoToDisplay(resetValue[1]));
    setStartError(null);
    setEndError(null);
    onSelect(resetValue);
    onCancel();
  };

  const hasInputErrors = !!startError || !!endError;

  const isApplyDisabled = useMemo(() => {
    if (hasInputErrors) return true;
    const hasStartDate = !disableStartDate ? value[0] !== null : true;
    const hasEndDate = !disableEndDate ? value[1] !== null : true;
    return !hasStartDate || !hasEndDate;
  }, [hasInputErrors, value, disableStartDate, disableEndDate]);

  const applyTime = (
    dateISO: ISOstring | null,
    [h, m]: [string, string],
    isEnd: boolean
  ): ISOstring => {
    if (!dateISO) return "";
    const datePart = dateISO.split("T")[0];
    const hh = (h === "" ? "0" : h).padStart(2, "0");
    const mm = (m === "" ? "0" : m).padStart(2, "0");
    // Default end (23:59) keeps the full end-of-day second precision.
    const seconds = isEnd && hh === "23" && mm === "59" ? "59.999" : "00.000";
    return `${datePart}T${hh}:${mm}:${seconds}Z`;
  };

  const handleOk = () => {
    if (isApplyDisabled) return;
    const startDate = value[0];
    const endDate = value[1];
    // When a date is disabled, its value may be null (managed externally by the
    // caller), but isApplyDisabled already accounts for that. Only check
    // non-disabled slots for presence.
    const startOk = disableStartDate || !!startDate;
    const endOk = disableEndDate || !!endDate;
    if (startOk && endOk) {
      if (showTime) {
        onSubmit([
          applyTime(startDate, startTime, false),
          applyTime(endDate, endTime, true),
        ]);
      } else {
        onSubmit([startDate ?? "", endDate ?? ""] as [ISOstring, ISOstring]);
      }
    }
  };

  // Stable time anchors (the date part is irrelevant — only the time is shown).
  // Each TimePicker initializes from these once and then lifts edits via
  // onChange, so feeding live time back in is avoided. Defaults: 00:00 / 23:59.
  const startTimeAnchor = useMemo(() => {
    const source = timeResetKey === 0 ? (initialValue?.[0] ?? null) : null;
    const [h, m] = getTimeParts(source, ["00", "00"]);
    return `2000-01-01T${h.padStart(2, "0")}:${m.padStart(2, "0")}:00.000Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeResetKey]);
  const endTimeAnchor = useMemo(() => {
    const source = timeResetKey === 0 ? (initialValue?.[1] ?? null) : null;
    const [h, m] = getTimeParts(source, ["23", "59"]);
    return `2000-01-01T${h.padStart(2, "0")}:${m.padStart(2, "0")}:00.000Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeResetKey]);

  const CalendarContainer = useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <div className={ownClasses.calendar}>
        <div>{children}</div>
      </div>
    ),
    [ownClasses.calendar]
  );

  return (
    <div className={cx(className, { [ownClasses.disabled]: disabled })}>
      {!hideInputs && (
        <div className={ownClasses.dateInputsRow}>
          <div className={ownClasses.dateInputWrapper}>
            <TextField
              variant="outlined"
              size="small"
              label="Start date"
              value={startInput}
              onChange={handleStartInputChange}
              placeholder="DD/MM/YYYY"
              disabled={disableStartDate}
              error={!!startError}
              helperText={startError ? `\u26A0 ${startError}` : undefined}
              className={ownClasses.dateTextField}
              fullWidth
              InputLabelProps={{ shrink: true }}
              FormHelperTextProps={{
                className: ownClasses.errorText,
              }}
            />
          </div>
          {!hideEndInput && (
            <div className={ownClasses.dateInputWrapper}>
              <TextField
                variant="outlined"
                size="small"
                label="End Date"
                value={endInput}
                onChange={handleEndInputChange}
                placeholder="DD/MM/YYYY"
                disabled={disableEndDate}
                error={!!endError}
                helperText={endError ? `\u26A0 ${endError}` : undefined}
                className={ownClasses.dateTextField}
                fullWidth
                InputLabelProps={{ shrink: true }}
                FormHelperTextProps={{
                  className: ownClasses.errorText,
                }}
              />
            </div>
          )}
        </div>
      )}
      <ReactDatePicker
        selectsRange
        selected={calendarSelected}
        onChange={handleOnChange}
        startDate={calendarRangeStart}
        endDate={calendarRangeEnd}
        minDate={minDateFakeUtc}
        maxDate={maxDateFakeUtc}
        // The calendar works in "fake UTC" dates, so hand callers back an ISO
        // string rather than leaking that convention out of this component.
        filterDate={
          isDateSelectable
            ? (date: Date) => isDateSelectable(convertDateToSameISO(date))
            : undefined
        }
        renderDayContents={
          dayTooltip
            ? (dayOfMonth: number, date?: Date) => {
                const tooltip = date
                  ? dayTooltip(convertDateToSameISO(date))
                  : undefined;
                return tooltip ? (
                  <span title={tooltip}>{dayOfMonth}</span>
                ) : (
                  dayOfMonth
                );
              }
            : undefined
        }
        calendarContainer={CalendarContainer}
        renderCustomHeader={DatePickerHeader}
        formatWeekDay={(weekDay) => weekDay.charAt(0)}
        showPopperArrow={false}
        calendarClassName={cx(ownClasses.calendar)}
        inline={inline}
        disabled={disabled}
        disabledKeyboardNavigation
      />
      {showTime && (
        <div className={ownClasses.timeSection}>
          <TimePicker
            key={`start-${timeResetKey}`}
            inline
            label="Start time"
            selectedDate={startTimeAnchor}
            onChange={handleStartTimeChange}
          />
          <TimePicker
            key={`end-${timeResetKey}`}
            inline
            label="End time"
            selectedDate={endTimeAnchor}
            onChange={handleEndTimeChange}
          />
        </div>
      )}
      <DatePickerFooter
        onClear={handleClear}
        onCancel={handleCancel}
        onOk={handleOk}
        disabledApply={isApplyDisabled}
      />
    </div>
  );
};

export default RangeDatePicker;
